import * as React from 'react';

import { useAppLibraryData } from '@/components/ConnectApps/Common/appLibraryData';
import { useFeedItemTitle } from '@/components/SourceCards/hooks/useFeedItemTitle';
import { useOpenFullScreen } from '@/components/SourceCards/hooks/useOpenFullScreen';
import { useFetchChannels } from '@/features/Channels/hooks/useFetchChannels';
import { NEEDL_UNIVERSE_ID } from '@/features/ChatBotV2/context';
import type { MetadataType } from '@/features/ChatBotV2/types';
import { useGetFilteredFeeds } from '@/hooks/query/useFetchFeeds';
import { useGetFilteredPublicApps } from '@/hooks/query/useFetchPublicApps';
import type { FeedItemType } from '@/types';
import { openInNewTab } from '@/utils/openInNewTab';

export const useGetSourceOfSession = ({
  metadata,
}: GetSourceOfSessionProps) => {
  const result = metadata.document || ({} as FeedItemType);
  const docTitle = useFeedItemTitle(result);
  const { openFullScreen } = useOpenFullScreen({
    result: {
      ...result,
      board_id: result?.board_id || 'home',
      channel_id: metadata?.channel_id,
    },
  });

  const boardId = ['home', NEEDL_UNIVERSE_ID].includes(
    metadata?.board_id as string
  )
    ? null
    : (metadata?.board_id as string);
  const channelId = metadata?.channel_id;
  const { isLoading: isFeedsLoading, data: filteredFeeds } =
    useGetFilteredFeeds();
  const { channels, isLoading: isChannelsLoading } = useFetchChannels();
  const { data: apps, isLoading: isApplibraryLoading } = useAppLibraryData();
  const { isLoading: isPublicAppsLoading, rssData } =
    useGetFilteredPublicApps(true);

  const isLoading = React.useMemo(() => {
    return (
      isFeedsLoading ||
      isApplibraryLoading ||
      isChannelsLoading ||
      isPublicAppsLoading
    );
  }, [
    isFeedsLoading,
    isApplibraryLoading,
    isChannelsLoading,
    isPublicAppsLoading,
  ]);

  const { isSorcePresent, sourceLabel } = React.useMemo(() => {
    if (isLoading) {
      return { isSorcePresent: false, sourceLabel: null };
    }

    const type = metadata.type;
    let sourceLabel = null;
    let isSorcePresent = false;

    switch (type) {
      case 'source': {
        const source = metadata?.source;
        const sourceInfoApps = apps?.data?.results?.find(
          (app) => app.label === source
        );
        const sourceInfoWebsites = rssData?.find(
          (website) => website.key === source
        );
        if (sourceInfoApps) {
          isSorcePresent = sourceInfoApps?.flag || false;
          sourceLabel = sourceInfoApps?.value || null;
        } else if (sourceInfoWebsites) {
          isSorcePresent = true;
          sourceLabel = sourceInfoWebsites?.label || null;
        } else {
          isSorcePresent = false;
          sourceLabel = null;
        }
        break;
      }
      case 'feed': {
        const feed = filteredFeeds?.find((feed) => feed.board_id === boardId);
        isSorcePresent = !!feed;
        sourceLabel = feed?.board_name || null;
        break;
      }
      case 'channel': {
        const channel = channels?.find((channel) => channel.id === channelId);
        isSorcePresent = !!channel;
        sourceLabel = channel?.name || null;
        break;
      }
      case 'document': {
        const document = metadata?.document;
        isSorcePresent = !!document;
        sourceLabel = docTitle;
        break;
      }
      default: {
        isSorcePresent = true;
        sourceLabel = null;
      }
    }

    return { isSorcePresent, sourceLabel };
  }, [
    apps,
    boardId,
    channelId,
    channels,
    filteredFeeds,
    isLoading,
    metadata,
    docTitle,
    rssData,
  ]);

  const openSource = React.useCallback(() => {
    if (isLoading) {
      return;
    }

    switch (metadata?.type) {
      case 'source': {
        openInNewTab(`/apps/${metadata?.source}`);
        break;
      }
      case 'feed': {
        openInNewTab(`/feeds/${metadata?.board_id}`);
        break;
      }
      case 'channel': {
        openInNewTab(`/channels/${metadata?.channel_id}`);
        break;
      }
      case 'document': {
        openFullScreen();
        break;
      }
      default: {
        break;
      }
    }
  }, [isLoading, metadata, openFullScreen]);

  return { isSorcePresent, sourceLabel, openSource, isLoading };
};

type GetSourceOfSessionProps = {
  metadata: MetadataType;
};
